import React, { useState } from 'react';
import {
  View,
  Text,
  Platform,
  ViewStyle,
  TextStyle,
  StyleSheet,
} from 'react-native';
import { useTheme } from '../context/themeContext';

// On native we use Picker from @react-native-picker/picker
// On web we render a native <select> element via the web-specific approach

export interface SelectOption {
  label: string;
  value: string | number;
}

interface SelectProps {
  label?: string;
  hint?: string;
  error?: string;
  options?: SelectOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'lg';
  style?: ViewStyle;
}

export default function Select({
  label,
  hint,
  error,
  options = [],
  value,
  onChange,
  placeholder,
  disabled = false,
  size = 'default',
  style,
}: SelectProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const [focused, setFocused] = useState(false);

  const heights: Record<'sm' | 'default' | 'lg', number> = {
    sm: 28,
    default: 34,
    lg: 40,
  };

  const fontSizes: Record<'sm' | 'default' | 'lg', number> = {
    sm: fontSize.sm,
    default: fontSize.md,
    lg: fontSize.lg,
  };

  const height = heights[size];
  const fSize = fontSizes[size];

  const borderColor = error
    ? colors.danger
    : focused
      ? colors.accent
      : colors.line;

  const focusShadow = focused && !error
    ? `0 0 0 3px ${colors.accentSoft}`
    : 'none';

  if (Platform.OS === 'web') {
    // Use native HTML select for web
    const webContainerStyle: ViewStyle & { [key: string]: any } = {
      height,
      borderRadius: radii.r2,
      borderWidth: 1,
      borderColor,
      backgroundColor: disabled ? colors.paper2 : colors.card,
      // @ts-ignore web only
      boxShadow: focused ? `0 0 0 3px ${colors.accentSoft}` : 'none',
      transition: 'border-color 100ms, box-shadow 100ms',
      overflow: 'hidden',
    };

    const webSelectStyle: any = {
      width: '100%',
      height: '100%',
      border: 'none',
      background: 'transparent',
      fontSize: fSize,
      fontFamily: theme.font.sans,
      color: value === undefined ? colors.ink4 : disabled ? colors.ink3 : colors.ink,
      paddingLeft: 12,
      paddingRight: 28,
      cursor: disabled ? 'not-allowed' : 'pointer',
      outline: 'none',
      appearance: 'none',
      backgroundImage: `linear-gradient(45deg, transparent 50%, ${colors.ink3} 50%), linear-gradient(135deg, ${colors.ink3} 50%, transparent 50%)`,
      backgroundPosition: 'calc(100% - 14px) 50%, calc(100% - 9px) 50%',
      backgroundSize: '5px 5px, 5px 5px',
      backgroundRepeat: 'no-repeat',
    };

    const selectEl = (
      <View style={[webContainerStyle as ViewStyle, style]}>
        {/* @ts-ignore */}
        <select
          value={value !== undefined ? String(value) : ''}
          onChange={(e: any) => onChange?.(e.target.value)}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={webSelectStyle}
        >
          {placeholder && (
            // @ts-ignore
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            // @ts-ignore
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      </View>
    );

    if (!label && !hint && !error) return selectEl;

    return (
      <View style={styles.fieldWrapper}>
        {label && (
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, fontFamily: theme.font.sans, marginBottom: 4 }}>
            {label}
          </Text>
        )}
        {selectEl}
        {error ? (
          <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontFamily: theme.font.sans, marginTop: 4 }}>{error}</Text>
        ) : hint ? (
          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>{hint}</Text>
        ) : null}
      </View>
    );
  }

  // Native — use Picker
  // Dynamically require to avoid web bundling issues
  let PickerComponent: any = null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@react-native-picker/picker');
    PickerComponent = mod.Picker;
  } catch {
    // Fallback: render nothing if Picker not available
    return null;
  }

  const containerStyle: ViewStyle = {
    height,
    borderRadius: radii.r2,
    borderWidth: 1,
    borderColor,
    backgroundColor: disabled ? colors.paper2 : colors.card,
    justifyContent: 'center',
    overflow: 'hidden',
  };

  const pickerEl = (
    <View style={[containerStyle, style]}>
      <PickerComponent
        selectedValue={value}
        onValueChange={(itemValue: string | number) => onChange?.(itemValue)}
        enabled={!disabled}
        style={{
          color: disabled ? colors.ink3 : colors.ink,
          fontFamily: theme.font.sans,
          fontSize: fSize,
          height,
        }}
        dropdownIconColor={colors.ink3}
      >
        {placeholder && (
          <PickerComponent.Item label={placeholder} value={undefined} color={colors.ink4} />
        )}
        {options.map((o) => (
          <PickerComponent.Item key={String(o.value)} label={o.label} value={o.value} />
        ))}
      </PickerComponent>
    </View>
  );

  if (!label && !hint && !error) return pickerEl;

  return (
    <View style={styles.fieldWrapper}>
      {label && (
        <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, fontFamily: theme.font.sans, marginBottom: 4 }}>
          {label}
        </Text>
      )}
      {pickerEl}
      {error ? (
        <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontFamily: theme.font.sans, marginTop: 4 }}>{error}</Text>
      ) : hint ? (
        <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>{hint}</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  fieldWrapper: {
    flexDirection: 'column',
  },
});
